'use client';

import { useState, useEffect, useRef, useCallback } from 'react';

export interface ObjectRefResult {
  id: string;
  title: string;
  entityType: string | null;
}

interface ObjectRefPopupProps {
  open: boolean;
  onClose: () => void;
  onSelect: (ref: ObjectRefResult) => void;
  searchEntities: (query: string) => Promise<ObjectRefResult[]>;
}

export function ObjectRefPopup({ open, onClose, onSelect, searchEntities }: ObjectRefPopupProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ObjectRefResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (!q) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await searchEntities(q);
        setResults(r);
        setSelectedIndex(0);
      } catch {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open, searchEntities]);

  const choose = useCallback((r: ObjectRefResult) => {
    onSelect(r);
    onClose();
  }, [onSelect, onClose]);

  if (!open) return null;

  return (
    <div className="gam-objref-popup__backdrop" onClick={onClose}>
      <div className="gam-objref-popup" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="gam-objref-popup__input"
          placeholder="חפש ישות או מסמך..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { onClose(); }
            else if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex((i) => Math.min(i + 1, results.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex((i) => Math.max(i - 1, 0)); }
            else if (e.key === 'Enter') { e.preventDefault(); const r = results[selectedIndex]; if (r) choose(r); }
          }}
        />
        <div className="gam-objref-popup__list">
          {results.length === 0 && query.trim() !== '' && (
            <div className="gam-objref-popup__empty">אין תוצאות</div>
          )}
          {results.map((r, i) => (
            <button
              key={r.id}
              type="button"
              className={`gam-objref-popup__item${i === selectedIndex ? ' is-active' : ''}`}
              onMouseEnter={() => setSelectedIndex(i)}
              onClick={() => choose(r)}
            >
              <span className="gam-objref-popup__item-title">{r.title || r.id}</span>
              {r.entityType && <span className="gam-objref-popup__item-type">{r.entityType}</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
